import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AppRoutes, UserScopedCacheService } from '@mri-platform/dsg/core';
import { Dataset, Report } from '@mri-platform/dsg/entity-state';
import { ClaimsAuthzService, CrudAction } from '@mri-platform/resource-authz';
import { AskAiFrameConfig, ChatDashboardSection, FeatureFlagsService, RouteMetadata } from '@mri-platform/shared/core';
import { MainMenuItemService } from '@mri-platform/shared/shell';
import {
  AskAgoraFrameMenuItem,
  DrawerItem,
  DrawerMenuItem,
  isAskAgoraFrameComponent,
  LinkMenuItem,
  MainNavService,
  MenuItem
} from '@mri-platform/shared/ui-main-nav-bar';
import { AuthService } from '@mri/angular-wfe-proxy-oidc';
import { combineLatest, distinctUntilChanged, filter, iif, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { AskAgoraService } from './ask-agora-service';
import { DashboardsAvailabilityService } from './dashboards-availability.service';
import { ModuleViewsMenuService } from './module-views-menu.service';

const permissionMap = {
  viewReport: Report.authorization[CrudAction.Read],
  viewDataset: Dataset.authorization[CrudAction.Read]
};

@Injectable()
export class AppMainMenuItemService implements MainMenuItemService {
  private readonly commandCenter: LinkMenuItem = {
    iconName: 'layers',
    title: 'Command Center',
    ...AppRoutes.CommandCenter.toMenuInfo()
  };

  private readonly portfolioExplorer: LinkMenuItem = {
    iconName: 'hub',
    customIcon: 'portfolio-explorer',
    title: 'Portfolio Explorer',
    ...AppRoutes.PortfolioExplorer.toMenuInfo()
  };

  private readonly dashboards: LinkMenuItem = {
    iconName: 'report',
    title: 'Dashboards',
    supportsToggle: true,
    customIcon: 'dashboards',
    ...AppRoutes.Dashboards.toMenuInfo()
  };

  private readonly accessManagement: LinkMenuItem = {
    iconName: 'dashboard_customize',
    title: 'Access Management',
    ...AppRoutes.AccessManagement.toMenuInfo()
  };

  private readonly mriSettings: DrawerMenuItem = {
    iconName: 'settings',
    title: 'Admin Settings',
    cssClass: ['settings-icon'],
    links: [
      { text: 'User security settings', ...AppRoutes.UserSettings.toMenuInfo() },
      { text: 'Data sources', ...AppRoutes.DataSources.toMenuInfo() },
      { text: 'AI usage', ...AppRoutes.AiUsage.toMenuInfo() },
      { text: 'Reconciliation screen', ...AppRoutes.Reconciliation.toMenuInfo() },
      { text: 'Client Onboard Configuration', ...AppRoutes.ClientOnboardConfig.toMenuInfo() }
    ]
  };

  private readonly insightEvals: LinkMenuItem = {
    iconName: 'grid-person',
    title: 'Insight Evals',
    cssClass: ['insight-evals-icon'],
    ...AppRoutes.InsightEvals.toMenuInfo()
  };

  private readonly about: LinkMenuItem = {
    iconName: 'information-circle-outlined',
    title: 'About',
    cssClass: ['help-center-icon'],
    ...AppRoutes.About.toMenuInfo(),
    opener: (_: string) =>
      this.router.navigate(['', { outlets: { modal: [AppRoutes.About.path, 'details'] } }], {
        queryParamsHandling: 'preserve'
      })
  };

  private readonly menuItems$: Observable<MenuItem<RouteMetadata>[]>;

  constructor(
    private readonly router: Router,
    private readonly askAgoraService: AskAgoraService,
    private readonly askAiFrameConfig: AskAiFrameConfig,
    private readonly cacheService: UserScopedCacheService,
    private readonly claimAuthzService: ClaimsAuthzService,
    private readonly authService: AuthService,
    private readonly featureFlagService: FeatureFlagsService,
    private readonly dashboardsAvailabilityService: DashboardsAvailabilityService,
    private readonly mainNavService: MainNavService,
    private readonly moduleViewsMenuService: ModuleViewsMenuService
  ) {
    this.menuItems$ = combineLatest({
      frameSources: this.createGetFrameSources$(),
      ddChatEnabled: this.featureFlagService.isEnabled$('ddChatEnabled'),
      showDashboards: this.dashboardsAvailabilityService.hasDashboardsCachedSafe$,
      commandCenterEnabled: this.featureFlagService.isEnabled$('commandCenter'),
      portfolioExplorerEnabled: this.featureFlagService.isEnabled$('portfolioExplorer'),
      moduleViewsEnabled: this.featureFlagService.isEnabled$('moduleViews'),
      moduleViewLinks: this.moduleViewsMenuService.getModuleViewLinks$(),
      isMriUser: this.authService.currentUser$.pipe(
        map(user => user.email?.toLowerCase().endsWith('@mrisoftware.com') ?? false)
      ),
      insightEvalsEnabled: this.featureFlagService.isEnabled$('insightEvals')
    }).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map(this.getMenuItems.bind(this)),
      shareReplay(1)
    );

    combineLatest({
      menuItems: this.menuItems$,
      hasDashboards: this.dashboardsAvailabilityService.hasDashboards$,
      commandCenterEnabled: this.featureFlagService.isEnabled$('commandCenter')
    })
      .pipe(
        filter(({ hasDashboards, commandCenterEnabled }) => !hasDashboards && !commandCenterEnabled),
        map(({ menuItems }) => menuItems.find(isAskAgoraFrameComponent) as AskAgoraFrameMenuItem | undefined),
        filter((item): item is AskAgoraFrameMenuItem => !!item),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe(item => this.mainNavService.activate(item));
  }

  private getMenuItems({
    frameSources,
    ddChatEnabled,
    showDashboards,
    portfolioExplorerEnabled,
    moduleViewsEnabled,
    moduleViewLinks,
    isMriUser,
    insightEvalsEnabled
  }: {
    frameSources: ChatDashboardSection | null;
    ddChatEnabled: boolean;
    showDashboards: boolean;
    commandCenterEnabled: boolean;
    portfolioExplorerEnabled: boolean;
    moduleViewsEnabled: boolean;
    moduleViewLinks: DrawerItem[];
    isMriUser: boolean;
    insightEvalsEnabled: boolean;
  }): MenuItem<RouteMetadata>[] {
    return [
      frameSources ? this.askAiFrameMenuItem(frameSources, ddChatEnabled) : undefined,
      this.commandCenter,
      portfolioExplorerEnabled ? this.portfolioExplorer : undefined,
      moduleViewsEnabled ? this.buildModuleViewsMenuItem(moduleViewLinks) : undefined,
      showDashboards ? this.dashboards : undefined,
      this.accessManagement,
      this.mriSettings,
      isMriUser && insightEvalsEnabled ? this.insightEvals : undefined,
      this.about
    ].filter(item => !!item);
  }

  private buildModuleViewsMenuItem(viewLinks: DrawerItem[]): DrawerMenuItem {
    return {
      iconName: 'dashboard',
      customIcon: 'module-view',
      title: 'Views',
      links: [
        { text: 'Create View', url: `/${AppRoutes.CreateModuleView.path}`, suffixIcon: 'add_circle' },
        ...viewLinks
      ]
    };
  }

  private readonly askAiFrameMenuItem = (
    frameSources: ChatDashboardSection,
    ddChatEnabled: boolean
  ): AskAgoraFrameMenuItem<RouteMetadata> => ({
    embedAppBaseUrl: this.askAiFrameConfig.embedAppBaseUrl,
    ddChatBaseUrl: this.askAiFrameConfig.ddChatBaseUrl,
    cdnUrl: this.askAiFrameConfig.cdnUrl,
    iconName: '10k',
    title: 'Ask Agora',
    metadata: { featureName: 'askAgoraFrame', authzContext: permissionMap.viewReport },
    frameSources,
    isAskAgoraFrame: true,
    ddChatEnabled
  });

  private createGetFrameSources$() {
    return combineLatest({
      scope: this.cacheService.scope$,
      ddChatEnabled: this.featureFlagService.isEnabled$('ddChatEnabled'),
      user: this.authService.userLoaded$
    }).pipe(
      switchMap(({ ddChatEnabled, user: { permissions } }) =>
        iif(
          () =>
            ddChatEnabled
              ? this.claimAuthzService.checkAccess(permissionMap.viewDataset, permissions).isGranted
              : this.claimAuthzService.checkAccess(permissionMap.viewReport, permissions).isGranted,
          this.askAgoraService.getAskAgoraReportsAndDatasets$(ddChatEnabled),
          of(null)
        )
      )
    );
  }

  get items() {
    return this.menuItems$;
  }
}
